import { http } from '@api/client';
import { paramterize, pickResponse, pickResult } from '@api/utils';
import { CompanyServiceMaster } from '@lib/service-management';
import {
  ServiceManagementFilterParams,
  // ServiceManagementSearchResult,
  ServiceManagementResult,
  FilterResponse,
  SearchServiceManagementQuery,
  SearchServiceManagementResponse,
  ServicesAutoCompleteResult,
} from './types';
import { map } from 'rxjs/operators';

/**
 * Get latest deals or search and get the fitered deals.
 * @param query The query params to pass to the url.
 */
export function search(query: SearchServiceManagementQuery, vtr_id: number) {
  const params = paramterize(query);
  return http
    .get<SearchServiceManagementResponse>(`/company-services-master/search/${vtr_id}`, {
      params,
    })
    .pipe(pickResponse());
}

/**
 * Get the deal details from the server.
 * @param id The id of the deal to get
 */
export function get(vtr_id: number) {
  return http.get<ServiceManagementResult>(`/company-services-master/${vtr_id}`).pipe(pickResult());
}

// /**
//  * Filter deals using dynamic operators.
//  * @param option Query options
//  */
// export function filter(
//   company_vtr_id: string,
//   {
//     filters = [],
//     pageSize: page_size = 10,
//     page = 1,
//     sortBy = [],
//   }: ServiceManagementFilterParams = {}
// ) {
//   const sort = sortBy.map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`).join(';');

//   const newFilters = filters; //updateFiltersForLinkedOperator(filters);
//   const filtersMap: Record<string, string> = newFilters.reduce(
//     (map, f) =>
//       Object.assign(map, {
//         [f.id]: Object.entries(f.value)
//           .map(([op, v]) => {
//             return op === '$bw'
//               ? `$gte|||${v[0]};$lte|||${v[1]}`
//               : op === '$in' && f.id === 'service_provided_id'
//               ? `$in|||${v
//                   .split(',')
//                   .map(v => v.split('|||')[0])
//                   .join(',')}`
//               : `${op}|||${v}`;
//           })
//           .join(';'),
//       }),
//     {}
//   );

//   const params = paramterize({ page_size, page, sort, ...filtersMap });

//   return http
//     .get<FilterResponse>(`/company-content-relation/search/${company_vtr_id}`, { params })
//     .pipe(pickResponse());
// }

/**
 * Filter deals using dynamic operators.
 * @param option Query options
 */
export function filter({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: ServiceManagementFilterParams = {}) {
  const isServiceViewSort = (key: string) =>
    ['base_services_count', 'contents_associated_count'].includes(key);

  const sort_service_view = sortBy
    .filter(({ id }) => isServiceViewSort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort = sortBy
    .filter(({ id }) => !isServiceViewSort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  /////

  const newFilters = filters; //updateFiltersForLinkedOperator(filters);
  const filtersMap: Record<string, string> = newFilters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' && f.id === 'service_provided_id'
              ? `$in|||${v
                  .split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${v.trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({
    page_size,
    page,
    sort,
    sort_service_view,
    // sort_content_title,
    // sort_client_name,
    // sort_service_name,
    ...filtersMap,
  });

  return http
    .get<FilterResponse>(`/company-services-master/search`, { params })
    .pipe(pickResponse());
}

// /**
//  * Merge deals.
//  * @param data The merge request model.
//  */
// export function merge(data: DealMergeRequest) {
//   return http.put<DealResult>('/deals/related', data).pipe(pickResponse());
// }

/**
 * Updates the given deal.
 * @param deal The deal to update
 */
export function update(serviceData: Partial<CompanyServiceMaster>) {
  return http
    .put<CompanyServiceMaster>(`/company-services-master/${serviceData.vtr_id}`, serviceData)
    .pipe(pickResponse());
}

/**
 * Deletes the given deal.
 * @param deal The deal id or the actual deal.
 */
export function remove(serviceData: Partial<CompanyServiceMaster>) {
  return http
    .delete<CompanyServiceMaster>(
      `/company-services-master/${serviceData.vtr_id}`
      // , {
      //   data: { vtr_id: relationData.vtr_id,
      //     delete_reason: relationData.delete_reason },
      // }
    )
    .pipe(pickResponse());
}

/**
 * Creates the given deal.
 * @param deal The deal to create
 */
export function create(serviceData: Partial<CompanyServiceMaster>) {
  return http
    .post<ServiceManagementResult>(`/company-services-master`, serviceData)
    .pipe(pickResponse());
}

/**
 * Gets autocomplete for services
 */
export function autoCompleteForServices() {
  return http.get<ServicesAutoCompleteResult>(`/company-services-master-auto-complete`).pipe(
    pickResult(),
    map(x => {
      const allAutoCompleteServices = x['autocomplete-services'];
      const mappedServices = allAutoCompleteServices.map((x, index) => ({ id: index, service: x }));
      return mappedServices;
    })
  );
}

/**
 * Gets autocomplete for parent services based on servcie id
 */
export function autoCompleteForParentServices(vtr_id: number) {
  return http.get(`/company-services-all${vtr_id ? `/${vtr_id}` : ''}`).pipe(
    pickResult(),
    map(x => {
      const allAutoCompleteServices = x;
      const mappedServices = allAutoCompleteServices.map(({company_service_name="",vtr_id=""}, index) => ({ id: vtr_id, service: company_service_name }));
      return mappedServices;
    })
  );
}

/**
 * Get the service hierarchy details from the server.
 * @param service_vtr_id The id of the service to get hierarchy
 */
export function getHierarchy(service_vtr_id: number) {
  return http.get(`/company/service-full-hierarchy?service_vtr_id=${service_vtr_id}`).pipe(pickResult());
}